import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Space, Typography, Dropdown, Avatar } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  BarChartOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ScanOutlined,
  SwapOutlined,
  VideoCameraOutlined,
  SearchOutlined,
  LogoutOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { usePhotoRefresh } from '../context/PhotoRefreshContext';
import api from '../utils/api';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background: #f0f2f5;
  position: relative;
`;


const StyledHeader = styled(Header)`
  background: #ffffff !important;
  padding: 0 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 999;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;

  .logo-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    overflow: hidden;
  }

  .logo-text {
    font-weight: 700;
    font-size: 20px;
    color: #B4A0F7;
  }
`;

const StyledSider = styled(Sider)`
  background: #ffffff !important;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease !important;
  position: fixed !important;
  left: 0;
  top: 0;
  bottom: 0;
  height: 100vh;
  overflow: hidden;

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .ant-layout-sider-trigger {
    background: linear-gradient(135deg, #B4A0F7 0%, #CCBFF9 100%) !important;
  }
`;

const MenuWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(180, 160, 247, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(180, 160, 247, 0.5);
  }

  .ant-menu-item, .ant-menu-submenu-title {
    user-select: none;
    cursor: pointer;
  }

  .ant-menu-item-selected::before {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 4px;
    height: 100%;
    background: #7C3AED;
    border-radius: 0 4px 4px 0;
  }
`;

const UserProfileSection = styled.div`
  padding: 16px;
  border-top: 1px solid #f0f0f0;
  background: rgba(180, 160, 247, 0.05);
  flex-shrink: 0;

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(180, 160, 247, 0.1);
    }
  }

  .user-details {
    flex: 1;
    overflow: hidden;
  }

  .user-name {
    font-weight: 600;
    font-size: 14px;
    color: #2D3748;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-role {
    font-size: 12px;
    color: #718096;
  }
`;

const StyledContent = styled(Content)`
  margin: 0;
  padding: 24px;
  background: transparent;
  transition: all 0.3s ease;
`;

const MainContentLayout = styled(Layout)`
  margin-left: ${props => props.$collapsed ? '0' : '250px'};
  transition: margin-left 0.3s ease;
`;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userPhotoUrl, setUserPhotoUrl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { getPhotoTimestamp } = usePhotoRefresh();

  // Fetch user's profile photo with cache-busting timestamp
  // Also refresh when photo timestamp changes in context
  useEffect(() => {
    const fetchUserPhoto = async () => {
      if (user?.emp_id) {
        try {
          const contextTimestamp = getPhotoTimestamp(user.emp_id);
          let photoUrl;
          if (contextTimestamp) {
            // Use context timestamp if available (photo was just updated)
            photoUrl = api.getEmployeePhoto(user.emp_id, null, contextTimestamp);
          } else {
            // Fetch employee info to get photo_updated_at timestamp from database
            const response = await api.searchEmployees(user.emp_id);
            photoUrl = api.getEmployeePhoto(user.emp_id, null, response.photo_updated_at);
          }
          setUserPhotoUrl(photoUrl);
        } catch (error) {
          console.error('Failed to load user photo:', error);
          // Fallback to photo without cache-busting
          setUserPhotoUrl(api.getEmployeePhoto(user.emp_id));
        }
      } else {
        setUserPhotoUrl(null);
      }
    };

    fetchUserPhoto();
  }, [user?.emp_id, getPhotoTimestamp]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // eslint-disable-next-line no-unused-vars
  const handleUserMenuClick = ({ key }) => {
    if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'logout') {
      handleLogout();
    }
  };

  // Menu items based on user role
  const menuItems = isAdmin() ? [
    // Admin sees all menu items
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/live-stream',
      icon: <VideoCameraOutlined />,
      label: 'Live Stream',
    },
    {
      key: '/smile-challenge',
      icon: <SmileOutlined />,
      label: 'Smile Challenge',
    },
    {
      key: '/find-person',
      icon: <SearchOutlined />,
      label: 'Find Person',
    },
    {
      key: '/unknown-faces',
      icon: <UserOutlined />,
      label: 'Unknown Faces',
    },
    {
      key: '/employees',
      icon: <TeamOutlined />,
      label: 'Employees',
    },
    {
      key: '/crossings',
      icon: <SwapOutlined />,
      label: 'Crossings Report',
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: 'Analytics',
    },
    {
      key: '/users',
      icon: <UsergroupAddOutlined />,
      label: 'User Management',
    },
  ] : [
    // Regular users see only these items
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/unknown-faces',
      icon: <UserOutlined />,
      label: 'My Matches',
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: 'My Analytics',
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: handleLogout,
    },
  ];

  // Get page title from current route
  const getPageTitle = () => {
    const item = menuItems?.find(item => item.key === location.pathname);
    return item?.label || '';
  };

  return (
    <StyledLayout>
      <StyledSider
        trigger={null}
        collapsible
        collapsed={collapsed}
        collapsedWidth={0}
        width={250}
      >
        <div style={{ padding: '24px 16px' }}>
          <Logo onClick={() => navigate('/dashboard')}>
            <div className="logo-icon">
              <img src="/favicon.png" alt="Avaniko" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            {!collapsed && <div className="logo-text">Avaniko AI</div>}
          </Logo>
        </div>

        <MenuWrapper>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => {
              navigate(key);
            }}
            style={{
              background: 'transparent',
              border: 'none',
            }}
          />
        </MenuWrapper>

        {!collapsed && (
          <UserProfileSection>
            <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="topLeft">
              <div className="user-info">
                {userPhotoUrl ? (
                  <Avatar
                    src={userPhotoUrl}
                    style={{
                      flexShrink: 0,
                      border: '2px solid #fff',
                      boxShadow: '0 2px 8px rgba(180, 160, 247, 0.3)'
                    }}
                  />
                ) : (
                  <Avatar
                    style={{
                      background: '#B4A0F7',
                      flexShrink: 0,
                      fontSize: 16,
                      fontWeight: 600,
                      border: '2px solid #fff'
                    }}
                  >
                    {(user?.full_name || user?.username || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <div className="user-details">
                  <div className="user-name">{user?.full_name || user?.username}</div>
                  <div className="user-role">{isAdmin() ? 'Administrator' : 'User'}</div>
                </div>
              </div>
            </Dropdown>
          </UserProfileSection>
        )}
      </StyledSider>

      <MainContentLayout $collapsed={collapsed}>
        <StyledHeader>
          <Space>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                style: { fontSize: '18px', cursor: 'pointer' },
                onClick: () => setCollapsed(!collapsed),
              })}
            </motion.div>
            <Title level={4} style={{ margin: 0, color: '#2D3748' }}>
              {getPageTitle()}
            </Title>
          </Space>
        </StyledHeader>

        <StyledContent>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </StyledContent>
      </MainContentLayout>
    </StyledLayout>
  );
};

export default MainLayout;
